import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/ActivityLogsService';
import { ActivityLog } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';
import { buildCreateActivityLogInput, buildUpdateActivityLogInput } from '../services/buildActivityLogInput';

export const useActivityLog = (activityLogId?: string) => {
    const queryClient = useQueryClient();

    const activityLogQuery = useQuery({
        queryKey: ['activity-log', activityLogId],
        queryFn: () => fetch(activityLogId!),
        enabled: !!activityLogId
    });

    const createMutation = useMutation({
        mutationFn: (values: ActivityLog) => add(buildCreateActivityLogInput(values)),
        onSuccess: () => {
            notifySuccess('Usage record created');
            queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: ActivityLog) => {
            if (!activityLogId) throw new Error('activityLogId is required parameter');
            return update(activityLogId, buildUpdateActivityLogInput(values) as ActivityLog);
        },
        onSuccess: () => {
            notifySuccess('Usage record updated');
            queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
            queryClient.invalidateQueries({ queryKey: ['activity-log', activityLogId] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!activityLogId) throw new Error('activityLogId is required parameter');
            return deleteRecord(activityLogId);
        },
        onSuccess: () => {
            notifySuccess('Usage record deleted');
            queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    return {
        record: activityLogQuery.data,
        loading: activityLogQuery.isLoading,
        fetchRecord: activityLogQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};